'use client';

import {
  CircleF,
  GoogleMap,
  Marker,
  OverlayViewF,
  OVERLAY_MOUSE_TARGET,
} from '@react-google-maps/api';
import type { CompetitorNearbyPlaceDto } from '@up2stay/up2promo-api-client';
import { cn, useGoogleMapsLoader } from '@up2stay/up2promo-ui';
import { BadgeCheck, Locate, Minus, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  COMPETITOR_MAP_DEFAULT_ALONE_ZOOM,
  COMPETITOR_MAP_MAX_ZOOM,
  COMPETITOR_MAP_MIN_ZOOM,
  clampCompetitorFitZoom,
  competitorMapCanFit,
  competitorMapOpticalPadding,
  competitorMapSizeJumped,
  opticalPanPixels,
} from '@/core/components/reputation/competitor-map-camera';
import {
  groupCompetitorPins,
  nextZoomTowardCluster,
  type ClusterablePlace,
} from '@/core/components/reputation/competitor-map-clusters';
import {
  CompetitorPinOverlay,
  PinToneDot,
} from '@/core/components/reputation/competitor-map-pin-overlay';
import {
  buildClusterCountIcon,
  formatPinRating,
  pinToneVsYou,
  type CompetitorPinTone,
  type CompetitorPinVariant,
} from '@/core/components/reputation/competitor-map-pins';
import { competitorMapCoveragePoints } from '@/core/components/reputation/competitor-radius';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { usePreferencesStore } from '@/core/store/preferences.store';
import { getSystemTheme } from '@/core/utils/theme';

import { env } from '@/core/env';

type MapStyleEntry = {
  elementType?: string;
  featureType?: string;
  stylers: Array<{
    color?: string;
    visibility?: string;
    lightness?: number;
    saturation?: number;
    weight?: number;
  }>;
};

/** Soft Graphite canvas — matches `--theme-background` / DESIGN.md. */
const MAP_SURFACE_LIGHT = '#ececec';
/** Canvas-dark — matches DESIGN.md dark canvas. */
const MAP_SURFACE_DARK = '#1a1a1a';

/**
 * Status Board map chrome: quiet grayscale so rating chips (ink / signal red /
 * emerald) stay readable. No highway yellow, park green, or POI clutter.
 */
const STATUS_BOARD_MAP_STYLES_LIGHT: MapStyleEntry[] = [
  { elementType: 'geometry', stylers: [{ color: MAP_SURFACE_LIGHT }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b6b6b' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: MAP_SURFACE_LIGHT }],
  },
  { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ visibility: 'on' }, { color: '#5c5c5c' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.stroke',
    stylers: [{ visibility: 'on' }, { color: MAP_SURFACE_LIGHT }],
  },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#d4d4d4' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#8a8a8a' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#f0f0f0' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#e0e0e0' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#c6c6c6' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [{ color: '#fafafa' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#d8d8d8' }],
  },
  {
    featureType: 'water',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: MAP_SURFACE_LIGHT }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry',
    stylers: [{ color: '#e8e8e8' }],
  },
];

const STATUS_BOARD_MAP_STYLES_DARK: MapStyleEntry[] = [
  { elementType: 'geometry', stylers: [{ color: MAP_SURFACE_DARK }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9a9a9a' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: MAP_SURFACE_DARK }],
  },
  { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ visibility: 'on' }, { color: '#b0b0b0' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.stroke',
    stylers: [{ visibility: 'on' }, { color: MAP_SURFACE_DARK }],
  },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#2a2a2a' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#060606' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#8a8a8a' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#333333' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#3a3a3a' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#222222' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [{ color: '#262626' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#060606' }],
  },
  {
    featureType: 'water',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: MAP_SURFACE_DARK }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry',
    stylers: [{ color: '#222222' }],
  },
];

/** Minimum pad (~1.7 km) so a single pin still has breathing room. */
const MIN_BOUNDS_PAD_DEG = 0.015;
/** Extra fraction around the pin set so chips/labels aren’t clipped. */
const BOUNDS_PAD_FACTOR = 0.35;
/** When focusing a list row, zoom at least this far so the pin is readable. */
const FOCUS_MIN_ZOOM = 15;

/** Theater stays >= 360px on phones so far tracked pins stay framed (DEV-435). */
function competitorMapStageClass(theater: boolean): string {
  return theater
    ? 'h-[360px] min-h-[360px] md:h-[480px] md:min-h-[480px] lg:h-[560px] lg:min-h-[560px]'
    : 'h-[280px] min-h-[280px] sm:h-[360px] sm:min-h-[360px]';
}

export type CompetitorMapOwn = {
  name: string;
  latitude: number;
  longitude: number;
  /** Your location’s average rating — shown on the own chip. */
  rating?: number;
};

export type CompetitorMapLabels = {
  mapUnavailable: string;
  yourLocation: string;
  legendTracked?: string;
  legendSuggested?: string;
  legendThreat?: string;
  legendAhead?: string;
  legendEven?: string;
  /** `{count}` — title on a cluster circle. */
  clusterCount?: string;
  /** Recenter / show-all control next to +/-. */
  recenterMap?: string;
  /** Short label on your own pin ("Du"). */
  youShort?: string;
};

/** A pin on the map: a nearby place, or (no `place`) your own location. */
type MapPinItem = ClusterablePlace & { place?: CompetitorNearbyPlaceDto };
const OWN_PIN_ID = '__own__';

function mapSurfaceColor(isDark: boolean): string {
  return isDark ? MAP_SURFACE_DARK : MAP_SURFACE_LIGHT;
}

function MapZoomControl({
  map,
  onRecenter,
  recenterLabel,
}: {
  map: google.maps.Map | null;
  onRecenter: () => void;
  recenterLabel: string;
}) {
  const zoomIn = useCallback(() => {
    if (!map) return;
    const z = map.getZoom() ?? 14;
    map.setZoom(Math.min(COMPETITOR_MAP_MAX_ZOOM, z + 1));
  }, [map]);
  const zoomOut = useCallback(() => {
    if (!map) return;
    const z = map.getZoom() ?? 14;
    map.setZoom(Math.max(COMPETITOR_MAP_MIN_ZOOM, z - 1));
  }, [map]);
  if (!map) return null;
  return (
    <div className="absolute left-3 top-3 z-10 flex flex-col gap-1">
      <div className="flex flex-col gap-px overflow-hidden rounded-lg border border-border bg-card/95 shadow-sm backdrop-blur-sm">
        <button
          type="button"
          onClick={zoomIn}
          className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Zoom in"
        >
          <Plus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={zoomOut}
          className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Zoom out"
        >
          <Minus className="h-4 w-4" />
        </button>
      </div>
      <button
        type="button"
        onClick={onRecenter}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card/95 text-muted-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={recenterLabel}
        title={recenterLabel}
      >
        <Locate className="h-4 w-4" />
      </button>
    </div>
  );
}

function MapLegend({
  labels,
}: {
  labels: Pick<
    CompetitorMapLabels,
    | 'yourLocation'
    | 'legendTracked'
    | 'legendSuggested'
    | 'legendThreat'
    | 'legendAhead'
    | 'legendEven'
  >;
}) {
  if (!labels.legendTracked || !labels.legendSuggested) return null;
  return (
    <div
      className="pointer-events-none absolute right-3 top-3 z-10 flex max-w-[min(100%-1.5rem,15rem)] flex-col gap-2 rounded-xl border border-border/80 bg-card/95 p-2.5 text-[11px] font-medium text-foreground shadow-sm backdrop-blur-sm max-md:hidden"
      data-map-legend
    >
      <span className="flex items-center gap-2">
        <span className="inline-flex h-4 items-center rounded-full bg-[image:var(--brand-grad)] px-1.5 text-[9.5px] font-bold text-white">
          ★
        </span>
        {labels.yourLocation}
      </span>
      <span className="flex items-center gap-2">
        <span className="inline-flex h-4 items-center gap-0.5 rounded-full border-2 border-primary bg-card px-1 text-primary">
          <BadgeCheck className="size-2.5" aria-hidden />
        </span>
        {labels.legendTracked}
      </span>
      <span className="flex items-center gap-2">
        <span className="inline-flex h-4 w-6 rounded-full border border-border bg-card" />
        {labels.legendSuggested}
      </span>
      <span className="border-t border-border/70 pt-2 text-muted-foreground flex flex-col gap-1.5">
        <span className="flex items-center gap-2">
          <PinToneDot tone="threat" />
          {labels.legendThreat}
        </span>
        <span className="flex items-center gap-2">
          <PinToneDot tone="ahead" />
          {labels.legendAhead}
        </span>
        <span className="flex items-center gap-2">
          <PinToneDot tone="even" />
          {labels.legendEven}
        </span>
      </span>
    </div>
  );
}

function pinVariant(place: CompetitorNearbyPlaceDto): CompetitorPinVariant {
  return place.alreadyTracked ? 'tracked' : 'discoverable';
}

function padLatLngBounds(bounds: google.maps.LatLngBounds): void {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latSpan = Math.max(ne.lat() - sw.lat(), 0);
  const lngSpan = Math.max(ne.lng() - sw.lng(), 0);
  const latPad = Math.max(latSpan * BOUNDS_PAD_FACTOR, MIN_BOUNDS_PAD_DEG);
  const lngPad = Math.max(lngSpan * BOUNDS_PAD_FACTOR, MIN_BOUNDS_PAD_DEG);
  bounds.extend({ lat: ne.lat() + latPad, lng: ne.lng() + lngPad });
  bounds.extend({ lat: sw.lat() - latPad, lng: sw.lng() - lngPad });
}

function queryAncestor(
  stage: HTMLElement,
  selector: string,
): HTMLElement | null {
  let node: HTMLElement | null = stage;
  for (let i = 0; i < 5 && node; i++) {
    const found = node.querySelector(selector);
    if (found instanceof HTMLElement) return found;
    node = node.parentElement;
  }
  return null;
}

function readOpticalInsets(stage: HTMLElement | null): {
  overlayRightPx: number;
  overlayBottomPx: number;
} {
  if (!stage) return { overlayRightPx: 0, overlayBottomPx: 0 };
  const overlay = queryAncestor(stage, '[data-map-optical-overlay]');
  const legend = queryAncestor(stage, '[data-map-legend]');
  const overlayRight = overlay?.offsetWidth ?? 0;
  const legendRight = legend
    ? Math.ceil(legend.getBoundingClientRect().width) + 24
    : 0;
  return {
    overlayRightPx: Math.max(overlayRight, legendRight),
    overlayBottomPx: 0,
  };
}

export function CompetitorMap({
  own,
  places,
  selectedPlaceId,
  onSelectPlace,
  labels,
  className,
  theater = false,
  yourAverageRating,
  nearbyRadiusMeters,
}: {
  own: CompetitorMapOwn;
  places: CompetitorNearbyPlaceDto[];
  selectedPlaceId: string | null;
  onSelectPlace: (place: CompetitorNearbyPlaceDto | null) => void;
  labels: CompetitorMapLabels;
  className?: string;
  /** Taller stage for the Competitors theater page. */
  theater?: boolean;
  yourAverageRating?: number;
  nearbyRadiusMeters?: number;
}) {
  const stageMin = competitorMapStageClass(theater);
  const themeSetting = usePreferencesStore((s) => s.themeSetting);
  const isDark = useMemo(() => {
    const resolved =
      themeSetting === 'system' ? getSystemTheme() : themeSetting;
    return resolved === 'dark';
  }, [themeSetting]);

  const apiKey = env.VITE_GOOGLE_MAPS_API_KEY ?? '';
  const surface = mapSurfaceColor(isDark);
  const ownRating = own.rating ?? yourAverageRating;

  if (!apiKey) {
    return (
      <div
        data-testid="competitor-map-stage"
        className={cn(
          'flex items-center justify-center rounded-lg bg-muted/40 text-sm text-muted-foreground',
          stageMin,
          className,
        )}
      >
        {labels.mapUnavailable}
      </div>
    );
  }

  return (
    <div
      data-testid="competitor-map-stage"
      className={cn('relative overflow-hidden rounded-lg', stageMin, className)}
      style={{ backgroundColor: surface }}
    >
      <CompetitorMapCanvas
        apiKey={apiKey}
        own={own}
        ownRating={ownRating}
        places={places}
        selectedPlaceId={selectedPlaceId}
        onSelectPlace={onSelectPlace}
        isDark={isDark}
        surface={surface}
        yourLocationLabel={labels.yourLocation}
        youShortLabel={labels.youShort ?? labels.yourLocation}
        clusterCountLabel={labels.clusterCount ?? '{count}'}
        recenterLabel={labels.recenterMap ?? 'Show all pins'}
        stageMin={stageMin}
        yourAverageRating={yourAverageRating ?? ownRating}
        nearbyRadiusMeters={nearbyRadiusMeters}
      />
      <MapLegend labels={labels} />
    </div>
  );
}

function CompetitorMapCanvas({
  apiKey,
  own,
  ownRating,
  places,
  selectedPlaceId,
  onSelectPlace,
  isDark,
  surface,
  yourLocationLabel,
  youShortLabel,
  clusterCountLabel,
  recenterLabel,
  stageMin,
  yourAverageRating,
  nearbyRadiusMeters,
}: {
  apiKey: string;
  own: CompetitorMapOwn;
  ownRating?: number;
  places: CompetitorNearbyPlaceDto[];
  selectedPlaceId: string | null;
  onSelectPlace: (place: CompetitorNearbyPlaceDto | null) => void;
  isDark: boolean;
  surface: string;
  yourLocationLabel: string;
  youShortLabel: string;
  clusterCountLabel: string;
  recenterLabel: string;
  stageMin: string;
  yourAverageRating?: number;
  nearbyRadiusMeters?: number;
}) {
  const { isLoaded } = useGoogleMapsLoader(apiKey);
  const isMobile = useIsMobile();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [viewZoom, setViewZoom] = useState(COMPETITOR_MAP_DEFAULT_ALONE_ZOOM);
  // Cluster whose member list is open (only at max zoom, for pins in the same spot).
  const [openClusterId, setOpenClusterId] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const placesRef = useRef(places);
  const nearbyRadiusRef = useRef(nearbyRadiusMeters);
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const idleSyncListenerRef = useRef<google.maps.MapsEventListener | null>(
    null,
  );
  const clusterIdleListenerRef = useRef<google.maps.MapsEventListener | null>(
    null,
  );
  const clusterZoomTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastFitSizeRef = useRef({ width: 0, height: 0 });
  useEffect(() => {
    placesRef.current = places;
  });
  useEffect(() => {
    nearbyRadiusRef.current = nearbyRadiusMeters;
  });
  const center = useMemo(
    () => ({ lat: own.latitude, lng: own.longitude }),
    [own.latitude, own.longitude],
  );

  const syncViewZoom = useCallback((mapInstance: google.maps.Map) => {
    const zoom = mapInstance.getZoom();
    if (zoom == null) return;
    const rounded = Math.round(zoom);
    setViewZoom((prev) => (prev === rounded ? prev : rounded));
  }, []);

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      backgroundColor: surface,
      styles: isDark
        ? STATUS_BOARD_MAP_STYLES_DARK
        : STATUS_BOARD_MAP_STYLES_LIGHT,
      // Desktop: the wheel scrolls the page; zoom via Ctrl/⌘ + wheel or the
      // buttons. Touch keeps one-finger panning (DEV-822).
      gestureHandling: isMobile
        ? ('greedy' as const)
        : ('cooperative' as const),
      minZoom: COMPETITOR_MAP_MIN_ZOOM,
      maxZoom: COMPETITOR_MAP_MAX_ZOOM,
    }),
    [isDark, isMobile, surface],
  );

  const placesSignature = useMemo(
    () =>
      places
        .map(
          (place) =>
            `${place.placeId}:${place.latitude.toFixed(5)}:${place.longitude.toFixed(5)}`,
        )
        .join('|'),
    [places],
  );

  const fitBounds = useCallback(
    (mapInstance: google.maps.Map) => {
      const stage = stageRef.current;
      const width = stage?.clientWidth ?? 0;
      const height = stage?.clientHeight ?? 0;
      if (!competitorMapCanFit(width, height)) return;

      google.maps.event.trigger(mapInstance, 'resize');

      const coverage = competitorMapCoveragePoints({
        own: { latitude: center.lat, longitude: center.lng },
        places: placesRef.current,
        nearbyRadiusMeters: nearbyRadiusRef.current,
      });

      if (placesRef.current.length === 0) {
        mapInstance.setCenter(center);
        mapInstance.setZoom(COMPETITOR_MAP_DEFAULT_ALONE_ZOOM);
        lastFitSizeRef.current = { width, height };
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      for (const point of coverage) {
        bounds.extend({ lat: point.latitude, lng: point.longitude });
      }
      padLatLngBounds(bounds);

      const padding = competitorMapOpticalPadding({
        width,
        height,
        ...readOpticalInsets(stage),
      });

      if (idleListenerRef.current) {
        google.maps.event.removeListener(idleListenerRef.current);
      }
      mapInstance.fitBounds(bounds, padding);
      idleListenerRef.current = google.maps.event.addListenerOnce(
        mapInstance,
        'idle',
        () => {
          const zoom = mapInstance.getZoom();
          if (zoom == null) return;
          const next = clampCompetitorFitZoom(zoom);
          if (next !== zoom) {
            mapInstance.setZoom(next);
          }
          syncViewZoom(mapInstance);
        },
      );
      lastFitSizeRef.current = { width, height };
    },
    [center, syncViewZoom],
  );

  const resolveOpticalPadding = useCallback(() => {
    const stage = stageRef.current;
    const width = stage?.clientWidth ?? 0;
    const height = stage?.clientHeight ?? 0;
    return competitorMapOpticalPadding({
      width,
      height,
      ...readOpticalInsets(stage),
    });
  }, []);

  const focusLatLng = useCallback(
    (
      mapInstance: google.maps.Map,
      point: { lat: number; lng: number },
      zoom?: number,
    ) => {
      const padding = resolveOpticalPadding();
      const current = mapInstance.getZoom() ?? viewZoom;
      const targetZoom = Math.min(
        COMPETITOR_MAP_MAX_ZOOM,
        zoom ?? Math.max(current, FOCUS_MIN_ZOOM),
      );
      mapInstance.setZoom(targetZoom);
      mapInstance.setCenter(point);
      const pan = opticalPanPixels(padding);
      if (pan.x !== 0 || pan.y !== 0) {
        mapInstance.panBy(pan.x, pan.y);
      }
      setViewZoom(Math.round(targetZoom));
    },
    [resolveOpticalPadding, viewZoom],
  );

  const onMapLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      mapRef.current = mapInstance;
      setMap(mapInstance);
      if (idleSyncListenerRef.current) {
        google.maps.event.removeListener(idleSyncListenerRef.current);
      }
      idleSyncListenerRef.current = mapInstance.addListener('idle', () => {
        syncViewZoom(mapInstance);
      });
      fitBounds(mapInstance);
    },
    [fitBounds, syncViewZoom],
  );

  useEffect(() => {
    if (!map || !isLoaded) return;
    fitBounds(map);
  }, [fitBounds, isLoaded, map, placesSignature]);

  useEffect(() => {
    if (!map || !isLoaded) return;
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      const next = {
        width: stage.clientWidth,
        height: stage.clientHeight,
      };
      if (!competitorMapCanFit(next.width, next.height)) return;
      const prev = lastFitSizeRef.current;
      const grewFromInvalid = !competitorMapCanFit(prev.width, prev.height);
      if (grewFromInvalid || competitorMapSizeJumped(prev, next)) {
        fitBounds(map);
      }
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, [fitBounds, isLoaded, map]);

  // "You" is a pin like any other and may be absorbed into a cluster. Pins are
  // never displaced from their real position.
  const pinGroups = useMemo(() => {
    const items: MapPinItem[] = [
      {
        placeId: OWN_PIN_ID,
        latitude: own.latitude,
        longitude: own.longitude,
        isOwn: true,
      },
      ...places.map((place) => ({
        placeId: place.placeId,
        latitude: place.latitude,
        longitude: place.longitude,
        place,
      })),
    ];
    return groupCompetitorPins(items, viewZoom);
  }, [own.latitude, own.longitude, places, viewZoom]);

  const clusterIcons = useMemo(() => {
    if (!isLoaded) return new Map<string, google.maps.Icon>();
    const next = new Map<string, google.maps.Icon>();
    for (const group of pinGroups) {
      if (group.kind !== 'cluster') continue;
      next.set(
        group.id,
        buildClusterCountIcon(
          group.members.length,
          group.includesOwn ||
            group.members.some((member) => member.place?.alreadyTracked),
        ),
      );
    }
    return next;
  }, [isLoaded, pinGroups]);

  const openCluster = useMemo(() => {
    const group = pinGroups.find(
      (candidate) =>
        candidate.kind === 'cluster' && candidate.id === openClusterId,
    );
    return group?.kind === 'cluster' ? group : null;
  }, [openClusterId, pinGroups]);

  const handleClusterClick = useCallback(
    (group: Extract<(typeof pinGroups)[number], { kind: 'cluster' }>) => {
      const mapInstance = mapRef.current ?? map;
      if (!mapInstance) return;
      const currentZoom = mapInstance.getZoom() ?? viewZoom;
      const nextZoom = nextZoomTowardCluster(currentZoom);
      if (nextZoom == null) {
        // Max zoom and still overlapping (same building): let the user pick.
        setOpenClusterId(group.id);
        return;
      }

      setOpenClusterId(null);
      const clusterCenter = {
        lat: group.latitude,
        lng: group.longitude,
      };
      const bounds = new google.maps.LatLngBounds();
      for (const member of group.members) {
        bounds.extend({ lat: member.latitude, lng: member.longitude });
      }

      const clearWait = () => {
        if (clusterIdleListenerRef.current) {
          google.maps.event.removeListener(clusterIdleListenerRef.current);
          clusterIdleListenerRef.current = null;
        }
        if (clusterZoomTimeoutRef.current != null) {
          window.clearTimeout(clusterZoomTimeoutRef.current);
          clusterZoomTimeoutRef.current = null;
        }
      };
      clearWait();

      const viewBounds = mapInstance.getBounds();
      const alreadyOnScreen =
        viewBounds != null &&
        group.members.every((member) =>
          viewBounds.contains({ lat: member.latitude, lng: member.longitude }),
        );

      const settle = () => {
        clearWait();
        if (alreadyOnScreen) {
          syncViewZoom(mapInstance);
          return;
        }
        const after = mapInstance.getZoom() ?? nextZoom;
        const target = Math.min(
          COMPETITOR_MAP_MAX_ZOOM,
          Math.max(nextZoom, after),
        );
        focusLatLng(mapInstance, clusterCenter, target);
      };

      if (alreadyOnScreen) {
        focusLatLng(mapInstance, clusterCenter, nextZoom);
      } else {
        mapInstance.fitBounds(bounds, resolveOpticalPadding());
      }
      clusterIdleListenerRef.current = google.maps.event.addListenerOnce(
        mapInstance,
        'idle',
        settle,
      );
      clusterZoomTimeoutRef.current = setTimeout(settle, 320);
    },
    [focusLatLng, map, resolveOpticalPadding, syncViewZoom, viewZoom],
  );

  const selectedFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!map || !isLoaded) return;
    if (!selectedPlaceId) {
      selectedFocusRef.current = null;
      return;
    }
    const place = places.find((item) => item.placeId === selectedPlaceId);
    if (!place) return;
    if (selectedFocusRef.current === selectedPlaceId) return;
    selectedFocusRef.current = selectedPlaceId;
    const timer = window.setTimeout(() => {
      focusLatLng(map, { lat: place.latitude, lng: place.longitude });
    }, 160);
    return () => window.clearTimeout(timer);
  }, [focusLatLng, isLoaded, map, places, selectedPlaceId]);

  if (!isLoaded) {
    return (
      <div
        className={cn('h-full w-full', stageMin)}
        style={{ backgroundColor: surface }}
        aria-busy
      />
    );
  }

  const ownTitle =
    ownRating != null
      ? `${own.name || yourLocationLabel} · ${formatPinRating(ownRating)}`
      : own.name || yourLocationLabel;

  return (
    <>
      <div
        ref={stageRef}
        className={cn('h-full w-full', stageMin)}
        data-view-zoom={String(viewZoom)}
        data-pin-groups={pinGroups
          .map((group) =>
            group.kind === 'cluster' ? `c${group.members.length}` : 'p',
          )
          .join(',')}
      >
        <GoogleMap
          mapContainerClassName="h-full w-full"
          mapContainerStyle={{
            width: '100%',
            height: '100%',
            backgroundColor: surface,
          }}
          center={center}
          options={mapOptions}
          onLoad={onMapLoad}
          onUnmount={() => {
            if (idleSyncListenerRef.current) {
              google.maps.event.removeListener(idleSyncListenerRef.current);
              idleSyncListenerRef.current = null;
            }
            if (clusterIdleListenerRef.current) {
              google.maps.event.removeListener(clusterIdleListenerRef.current);
              clusterIdleListenerRef.current = null;
            }
            if (clusterZoomTimeoutRef.current != null) {
              window.clearTimeout(clusterZoomTimeoutRef.current);
              clusterZoomTimeoutRef.current = null;
            }
            mapRef.current = null;
            setMap(null);
          }}
          onClick={() => {
            setOpenClusterId(null);
            onSelectPlace(null);
          }}
        >
          {nearbyRadiusMeters ? (
            <CircleF
              center={center}
              radius={nearbyRadiusMeters}
              options={{
                clickable: false,
                strokeColor: '#3b82f6',
                strokeOpacity: 0.55,
                strokeWeight: 1.5,
                fillColor: '#3b82f6',
                fillOpacity: 0.05,
                zIndex: 1,
              }}
            />
          ) : null}
          {pinGroups.map((group) => {
            if (group.kind === 'cluster') {
              const title = group.includesOwn
                ? `${yourLocationLabel} + ${group.members.length - 1}`
                : clusterCountLabel.replace(
                    '{count}',
                    String(group.members.length),
                  );
              return (
                <Marker
                  key={`cluster:${group.id}`}
                  position={{ lat: group.latitude, lng: group.longitude }}
                  title={title}
                  icon={clusterIcons.get(group.id)}
                  zIndex={group.includesOwn ? 28 : 16}
                  onClick={(event) => {
                    event.domEvent?.stopPropagation?.();
                    handleClusterClick(group);
                  }}
                />
              );
            }
            const { item } = group;
            if (!item.place) {
              return (
                <CompetitorPinOverlay
                  key={OWN_PIN_ID}
                  latitude={own.latitude}
                  longitude={own.longitude}
                  variant="own"
                  tone="unknown"
                  ownLabel={youShortLabel}
                  rating={ownRating}
                  name={own.name || yourLocationLabel}
                  title={ownTitle}
                  zIndex={30}
                  clickable={false}
                />
              );
            }
            const place = item.place;
            const selected = selectedPlaceId === place.placeId;
            const tone = pinToneVsYou(place.rating, yourAverageRating);
            return (
              <CompetitorPinOverlay
                key={place.placeId}
                latitude={place.latitude}
                longitude={place.longitude}
                variant={pinVariant(place)}
                tone={tone}
                ownLabel=""
                rating={place.rating}
                name={place.name}
                selected={selected}
                title={`${place.name}${place.rating != null ? ` · ${formatPinRating(place.rating)}` : ''}${
                  tone === 'threat' ? ' ↑' : tone === 'ahead' ? ' ↓' : ''
                }`}
                zIndex={selected || place.alreadyTracked ? 12 : 4}
                onClick={() => onSelectPlace(place)}
              />
            );
          })}
          {openCluster ? (
            <OverlayViewF
              position={{
                lat: openCluster.latitude,
                lng: openCluster.longitude,
              }}
              mapPaneName={OVERLAY_MOUSE_TARGET}
            >
              <div
                className="absolute z-40 w-max min-w-40 max-w-64 -translate-x-1/2 translate-y-3 rounded-xl border bg-card p-1.5 text-[13px] shadow-lg"
                onClick={(event) => event.stopPropagation()}
              >
                {openCluster.members.map((member) =>
                  member.place ? (
                    <button
                      key={member.placeId}
                      type="button"
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-muted"
                      onClick={() => {
                        setOpenClusterId(null);
                        onSelectPlace(member.place ?? null);
                      }}
                    >
                      {member.place.alreadyTracked ? (
                        <BadgeCheck
                          className="size-3.5 shrink-0 text-primary"
                          aria-hidden
                        />
                      ) : null}
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {member.place.name}
                      </span>
                      <span className="text-muted-foreground tabular-nums">
                        ★ {formatPinRating(member.place.rating)}
                      </span>
                    </button>
                  ) : (
                    <p
                      key={member.placeId}
                      className="text-muted-foreground px-2.5 py-1.5 text-xs font-semibold"
                    >
                      {own.name || yourLocationLabel}
                    </p>
                  ),
                )}
              </div>
            </OverlayViewF>
          ) : null}
        </GoogleMap>
      </div>
      <MapZoomControl
        map={map}
        onRecenter={() => {
          const mapInstance = mapRef.current ?? map;
          if (mapInstance) fitBounds(mapInstance);
        }}
        recenterLabel={recenterLabel}
      />
    </>
  );
}
